import { useEffect, useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ScanLine, Trash2, Eye, Pencil, Sparkles, Printer } from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";

const CATEGORIES = ["Unstitched Suits", "Stitched Suits", "Dupattas", "Fabrics", "Embroidered Sets", "Accessories"];

// Compute selling price = cost * (1 + margin%)
const computeSelling = (cost, margin) => {
  const c = parseFloat(cost) || 0;
  const m = parseFloat(margin) || 0;
  return Math.round(c * (1 + m / 100) * 100) / 100;
};
// Compute MRP = selling / (1 - fake_discount%)
const computeMrp = (selling, fakeDisc) => {
  const s = parseFloat(selling) || 0;
  const d = Math.min(parseFloat(fakeDisc) || 0, 99.99);
  if (s <= 0) return 0;
  return Math.round((s / (1 - d / 100)) * 100) / 100;
};

export default function PurchaseBills() {
  const { activeBranchId, user, hasPerm } = useAuth();
  const isSuper = user.role === "super_admin";
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrText, setOcrText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ party_id: "", bill_number: "", bill_date: new Date().toISOString().slice(0, 10), items: [], notes: "", update_stock: true, party_discount_percent: 0, party_discount_amount: 0, round_off: null });

  const [scanResult, setScanResult] = useState(null);
  const [scannedPartyInfo, setScannedPartyInfo] = useState(null);  // {name, gstin} when scan party didn't match
  const [scanSummary, setScanSummary] = useState(null);  // {itemCount, newCount, matchedCount, provider} — shows post-fill confirmation banner inside the New Bill dialog
  const [createResult, setCreateResult] = useState(null);
  const [barcodeQty, setBarcodeQty] = useState({});  // {product_id: count}
  const [partyQuickOpen, setPartyQuickOpen] = useState(false);
  const [partyQuick, setPartyQuick] = useState({ party_name: "", gstin: "", legal_name: "", trade_name: "", state: "", taxpayer_type: "", address: "", city: "", pin: "", contact_person: "", phone: "", email: "", bank_name: "", bank_account_no: "", ifsc: "" });
  const [productQuickOpen, setProductQuickOpen] = useState(false);
  const [productQuick, setProductQuick] = useState({ name: "", barcode: "", sku: "", category: "Unstitched Suits", brand: "", fabric_type: "", color: "", size: "", design_code: "", hsn_code: "", cost_price: 0, gst_percent: 5, margin_percent: 30, fake_discount_percent: 10, mrp: 0, stock: 0, low_stock_alert: 5, target_row: null });
  const [orgDefaults, setOrgDefaults] = useState({ margin_percent: 30, fake_discount_percent: 10 });
  const [reloadKey, setReloadKey] = useState(0);

  const load = async () => {
    setReloadKey((k) => k + 1);  // triggers DataTable refresh
    const [pt, pr, settings] = await Promise.all([
      api.get("/parties"),
      api.get("/products"),
      api.get("/organisation/settings").catch(() => ({ data: {} })),
    ]);
    setParties(Array.isArray(pt.data) ? pt.data : (pt.data.rows || []));
    setProducts(Array.isArray(pr.data) ? pr.data : (pr.data.rows || []));
    if (settings.data) setOrgDefaults({
      margin_percent: settings.data.default_margin_percent ?? 30,
      fake_discount_percent: settings.data.default_fake_discount_percent ?? 10,
    });
  };
  useEffect(() => { load(); }, []);

  const addRow = () => setForm({ ...form, items: [...form.items, { product_id: "", name: "", description: "", barcode: "", hsn_code: "", quantity: 1, unit_price: 0, gst_percent: 5, discount_percent: 0, margin_percent: orgDefaults.margin_percent, fake_discount_percent: orgDefaults.fake_discount_percent }] });
  const updateRow = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const removeRow = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submitQuickParty = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        branch_id: activeBranchId,
        ...partyQuick,
        trade_name: partyQuick.trade_name || partyQuick.party_name,
        legal_name: partyQuick.legal_name || partyQuick.party_name,
      };
      const { data } = await api.post("/parties", payload);
      toast.success("Party added");
      setParties([...parties, data]);
      setForm({ ...form, party_id: data.id });
      setScannedPartyInfo(null);
      setPartyQuickOpen(false);
      setPartyQuick({ party_name: "", gstin: "", legal_name: "", trade_name: "", state: "", taxpayer_type: "", address: "", city: "", pin: "", contact_person: "", phone: "", email: "", bank_name: "", bank_account_no: "", ifsc: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitQuickProduct = async (e) => {
    e.preventDefault();
    try {
      const body = {
        branch_id: activeBranchId,
        name: productQuick.name,
        sku: productQuick.sku,
        category: productQuick.category,
        brand: productQuick.brand,
        fabric_type: productQuick.fabric_type,
        color: productQuick.color,
        size: productQuick.size,
        design_code: productQuick.design_code,
        hsn_code: productQuick.hsn_code,
        cost_price: parseFloat(productQuick.cost_price) || 0,
        gst_percent: parseFloat(productQuick.gst_percent) || 5,
        margin_percent: parseFloat(productQuick.margin_percent) || 30,
        fake_discount_percent: parseFloat(productQuick.fake_discount_percent) || 10,
        mrp: parseFloat(productQuick.mrp) || 0,
        stock: parseInt(productQuick.stock) || 0,
        low_stock_alert: parseInt(productQuick.low_stock_alert) || 5,
      };
      if (productQuick.barcode) body.barcode = productQuick.barcode;
      const { data } = await api.post("/products", body);
      toast.success(`Product added · ${data.barcode}`);
      setProducts([...products, data]);
      // Auto-link to target row
      if (productQuick.target_row != null) {
        updateRow(productQuick.target_row, {
          product_id: data.id, name: data.name, barcode: data.barcode,
          hsn_code: data.hsn_code, gst_percent: data.gst_percent,
          margin_percent: data.margin_percent, fake_discount_percent: data.fake_discount_percent,
        });
      }
      setProductQuickOpen(false);
      setProductQuick({ name: "", barcode: "", sku: "", category: "Unstitched Suits", brand: "", fabric_type: "", color: "", size: "", design_code: "", hsn_code: "", cost_price: 0, gst_percent: 5, margin_percent: 30, fake_discount_percent: 10, mrp: 0, stock: 0, low_stock_alert: 5, target_row: null });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async (e, saveDraft = false) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      const { data } = await api.post("/purchase-bills", { ...form, branch_id: activeBranchId, status: saveDraft ? "draft" : "posted" });
      const created = data.created_products || [];
      const updated = data.updated_products || [];
      toast.success(`${saveDraft ? "Saved as draft" : "Posted"} · ${created.length} new + ${updated.length} updated`);
      setOpen(false);
      setForm({ party_id: "", bill_number: "", bill_date: new Date().toISOString().slice(0, 10), items: [], notes: "", update_stock: true, party_discount_percent: 0, party_discount_amount: 0, round_off: null });
      // Drafts never update stock — and so we never offer to print barcodes for items
      // that aren't real sellable products yet. Stock + product creation happen when posting.
      if (!saveDraft) {
        const qtyMap = {};
        (data.items || []).forEach((it) => { if (it.product_id) qtyMap[it.product_id] = Math.max(1, Math.round(it.quantity)); });
        setBarcodeQty(qtyMap);
        setCreateResult({ bill: data, created, updated });
      } else {
        toast.info("Stock and barcodes will be available after you Post the draft.");
      }
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const postDraft = async (id) => {
    try {
      const { data } = await api.post(`/purchase-bills/${id}/post`);
      const created = data.created_products || [];
      const updated = data.updated_products || [];
      toast.success(`Posted · ${created.length} new + ${updated.length} updated`);
      // After posting a draft, open the barcode-print modal for any items now created/linked
      const qtyMap = {};
      (data.items || []).forEach((it) => { if (it.product_id) qtyMap[it.product_id] = Math.max(1, Math.round(it.quantity)); });
      setBarcodeQty(qtyMap);
      setCreateResult({ bill: { items: data.items || [] }, created, updated });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Reset the scan dialog state every time it opens — prevents stale data from previous scan
  const openScanDialog = (v) => {
    setOcrOpen(v);
    if (v) {
      setOcrText("");
      setScanResult(null);
      setOcrLoading(false);
    }
  };

  // Issue 1a — Print barcode labels for every line item on a posted bill in one click.
  // Uses the new server-side expand: POST /barcode-labels {purchase_bill_id} so the count per
  // item defaults to the bill qty and only items with a linked product (i.e. created/matched
  // during posting) are rendered.
  const printBillBarcodes = async (billId) => {
    try {
      const r = await api.post("/barcode-labels", { purchase_bill_id: billId }, { responseType: "blob" });
      const skipped = parseInt(r.headers["x-labels-skipped"] || "0", 10);
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(skipped ? `Labels rendered (${skipped} items skipped — no linked product yet).` : "Barcode labels rendered.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to render labels";
      toast.error(typeof msg === "string" ? msg : "Failed to render labels");
    }
  };

  const resetScan = () => {
    setOcrText("");
    setScanResult(null);
    setOcrLoading(false);
    const fi = document.querySelector('[data-testid="ocr-file-input"]');
    if (fi) fi.value = "";
  };

  const scanFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    const isPdf = name.endsWith(".pdf") || f.type === "application/pdf";
    const isImage = /\.(jpe?g|png|webp|heic|heif)$/.test(name) || (f.type || "").startsWith("image/");
    if (!isPdf && !isImage) {
      toast.error("Upload a PDF or image (JPG/PNG/WEBP/HEIC) of your supplier bill.");
      e.target.value = "";
      return;
    }
    setOcrLoading(true); setOcrText(""); setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/purchase-bills/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,  // Gemini Vision can take ~20-30s for complex bills
      });
      setScanResult(data);
      setOcrText(data.raw_text || "");
      const isFallback = (data.provider || "").includes("fallback");
      if (isFallback) {
        toast.warning(`Gemini unavailable — used local OCR (${data.provider}). ${data.items?.length || 0} items extracted.`);
      } else {
        toast.success(`Extracted ${data.items?.length || 0} line items via ${data.provider || "OCR"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Scan failed");
    }
    finally { setOcrLoading(false); }
  };

  const applyScan = () => {
    if (!scanResult) return;
    // Stash scanned party for the form-level banner & quick-add prefill (Issue 2)
    const partyName = scanResult.extracted?.party_name || "";
    const gstin = scanResult.extracted?.gstin || "";
    const noMatch = !scanResult.matched_party_id && (partyName || gstin);
    setScannedPartyInfo(noMatch ? { name: partyName, gstin } : null);
    if (noMatch) {
      // Pre-fill the quick-add party state so user can confirm with one click
      setPartyQuick((prev) => ({ ...prev, party_name: partyName, gstin, trade_name: partyName, legal_name: partyName }));
    }
    const items = (scanResult.items || []);
    const newCount = items.filter((it) => !it.matched_product_id).length;
    const matchedCount = items.length - newCount;
    setScanSummary({
      itemCount: items.length,
      newCount,
      matchedCount,
      provider: scanResult.provider || "OCR",
      partyMatched: !!scanResult.matched_party_id,
      partyName: scanResult.matched_party_name || partyName,
    });
    setForm({
      ...form,
      party_id: scanResult.matched_party_id || form.party_id,
      bill_number: scanResult.extracted?.invoice_no || form.bill_number,
      bill_date: scanResult.extracted?.bill_date || form.bill_date,
      items: items.map((it) => ({
        product_id: it.matched_product_id || "",
        name: it.name,
        description: it.description || "",
        barcode: it.matched_barcode || "",
        hsn_code: it.hsn_code || "",
        quantity: it.quantity,
        unit_price: it.unit_price,
        discount_percent: it.discount_percent || 0,
        gst_percent: it.gst_percent || 5,
        margin_percent: it.margin_percent ?? scanResult.defaults?.margin_percent ?? orgDefaults.margin_percent,
        fake_discount_percent: it.fake_discount_percent ?? scanResult.defaults?.fake_discount_percent ?? orgDefaults.fake_discount_percent,
        _from_scan: true,  // visual highlight flag
      })),
    });
    setOcrOpen(false);
    setOpen(true);
    toast.success(`${items.length} items pre-filled. Review margin% & MRP, then confirm.`);
    // Auto-scroll the New Bill dialog to the items table once it opens
    setTimeout(() => {
      const itemsEl = document.querySelector('[data-testid="pur-items-section"]');
      if (itemsEl) itemsEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  return (
    <div className="space-y-6" data-testid="purchase-bills-page">
      <div className="flex justify-end gap-2">
        {hasPerm("purchase_bill_scan") && (
          <Dialog open={ocrOpen} onOpenChange={openScanDialog}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-none" data-testid="scan-bill-btn"><ScanLine className="w-4 h-4 mr-2" />Scan Bill (PDF / Photo)</Button></DialogTrigger>
            <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>Scan Purchase Bill (PDF or Image)</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex justify-between items-end gap-2">
                  <div className="flex-1">
                    <Label>Upload supplier bill — PDF or photo (JPG / PNG / WEBP / HEIC)</Label>
                    <Input type="file" accept="application/pdf,.pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={scanFile} className="rounded-none mt-1" data-testid="ocr-file-input" />
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={resetScan} className="rounded-none" data-testid="reset-scan-btn">Reset</Button>
                </div>
                <div className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />Powered by Gemini 2.5 Flash — auto-falls-back to local OCR if budget is exhausted.
                </div>
                {ocrLoading && <div className="text-sm text-slate-500">Reading bill via Gemini AI — typically 15–30 seconds for complex bills…</div>}
                {scanResult && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 border border-slate-200">
                      <div><span className="text-slate-500">Party:</span> <b>{scanResult.matched_party_name || scanResult.extracted?.party_name || "—"}</b></div>
                      <div><span className="text-slate-500">GSTIN:</span> {scanResult.extracted?.gstin || "—"}</div>
                      <div><span className="text-slate-500">Invoice #:</span> {scanResult.extracted?.invoice_no || "—"}</div>
                      <div><span className="text-slate-500">Date:</span> {scanResult.extracted?.bill_date || "—"}</div>
                      <div className="col-span-2 text-[10px] text-slate-400 uppercase tracking-widest">Source: {scanResult.provider}</div>
                    </div>
                    <div className="border border-slate-200 max-h-64 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="p-2 text-left">Status</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">HSN</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">GST%</th></tr></thead>
                        <tbody>
                          {(scanResult.items || []).map((it, i) => (
                            <tr key={i} className="border-b border-slate-100" data-testid={`scan-row-${i}`}>
                              <td className="p-2">{it.is_new_product ? <span className="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 uppercase tracking-widest">New</span> : <span className="bg-green-100 text-green-800 text-[10px] px-2 py-0.5 uppercase tracking-widest">Match</span>}</td>
                              <td className="p-2">{it.name}</td>
                              <td className="p-2">{it.hsn_code}</td>
                              <td className="p-2 text-right">{it.quantity}</td>
                              <td className="p-2 text-right">{formatINR(it.unit_price)}</td>
                              <td className="p-2 text-right">{it.gst_percent}</td>
                            </tr>
                          ))}
                          {(!scanResult.items || scanResult.items.length === 0) && <tr><td colSpan={6} className="p-4 text-center text-slate-500">No line items extracted.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    <Button onClick={applyScan} className="bg-slate-900 hover:bg-slate-800 rounded-none w-full" data-testid="apply-scan-btn">Pre-fill Bill Form & Edit</Button>
                  </div>
                )}
                {ocrText && !scanResult && (
                  <div>
                    <Label>Raw text preview</Label>
                    <textarea value={ocrText} readOnly className="w-full h-48 border border-slate-200 rounded-none p-3 text-xs font-mono" data-testid="ocr-result" />
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setScannedPartyInfo(null); setScanSummary(null); } }}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-purchase-btn"><Plus className="w-4 h-4 mr-2" />Add Purchase Bill</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-6xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>New Purchase Bill</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              {scanSummary && (
                <div className="border border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 p-4 flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="scan-summary-banner">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-green-900">Bill scanned and pre-filled successfully</div>
                      <div className="text-xs text-green-800 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span><b>{scanSummary.itemCount}</b> items</span>
                        <span className="text-amber-700">· <b>{scanSummary.newCount}</b> new</span>
                        <span className="text-green-700">· <b>{scanSummary.matchedCount}</b> matched</span>
                        <span>· Party: <b>{scanSummary.partyMatched ? `${scanSummary.partyName} ✓` : "not matched"}</b></span>
                        <span className="text-slate-500 uppercase tracking-widest text-[10px]">via {scanSummary.provider}</span>
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1">Review margin %, fake-disc % and the live MRP per row below, then click Post.</div>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="rounded-none text-green-800" onClick={() => setScanSummary(null)} data-testid="scan-summary-dismiss">Got it</Button>
                </div>
              )}
              {scannedPartyInfo && !form.party_id && (
                <div className="border border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-3" data-testid="scanned-party-banner">
                  <div className="text-xs text-amber-900">
                    <div className="uppercase tracking-widest text-[10px] text-amber-700">Scanned party — not yet in your master list</div>
                    <div className="font-bold text-sm mt-0.5">{scannedPartyInfo.name || "—"}</div>
                    {scannedPartyInfo.gstin && <div className="font-mono text-[11px] mt-0.5">GSTIN: {scannedPartyInfo.gstin}</div>}
                  </div>
                  <Button type="button" size="sm" className="rounded-none bg-amber-700 hover:bg-amber-800" onClick={() => setPartyQuickOpen(true)} data-testid="create-scanned-party-btn">Create This Party</Button>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Party</Label>
                  <div className="flex gap-1">
                    <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="pur-party"><SelectValue placeholder="Select party" /></SelectTrigger>
                      <SelectContent>
                        {parties.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No parties — add one below.</div>}
                        {parties
                          .filter((p) => !activeBranchId || p.branch_id === activeBranchId)
                          .map((p) => (
                            <SelectItem key={p.id} value={p.id} data-testid={`pur-party-opt-${p.id}`}>
                              {p.party_name || p.trade_name || p.legal_name || p.gstin}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" className="rounded-none mt-1 h-10 w-10" onClick={() => setPartyQuickOpen(true)} title="Quick add new party" data-testid="quick-add-party"><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div><Label>Bill #</Label><Input required value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} className="rounded-none mt-1" data-testid="pur-billno" /></div>
                <div><Label>Bill Date</Label><Input required type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} className="rounded-none mt-1" /></div>
              </div>
              <div className="border border-slate-200 overflow-x-auto" data-testid="pur-items-section">
                <div className="bg-slate-50 border-b border-slate-200 p-2 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-widest">Items {form.items.length > 0 && <span className="text-slate-400 ml-1">({form.items.length})</span>}</span>
                  <Button type="button" size="sm" onClick={addRow} className="rounded-none h-7" data-testid="add-pur-row"><Plus className="w-3 h-3 mr-1" />Row</Button>
                </div>
                <table className="w-full text-sm min-w-[1280px]">
                  <thead className="bg-slate-50 border-b border-slate-200"><tr>
                    <th className="p-2 text-left w-[180px]">Product</th>
                    <th className="p-2 text-left min-w-[200px]">Name</th>
                    <th className="p-2 text-left min-w-[180px]">Description</th>
                    <th className="p-2 w-[90px]">HSN</th>
                    <th className="p-2 w-[70px]">Qty</th>
                    <th className="p-2 w-[90px]">Rate</th>
                    <th className="p-2 w-[70px]">Disc%</th>
                    <th className="p-2 w-[70px]">GST%</th>
                    <th className="p-2 w-[80px]" title="Selling margin over cost">Margin%</th>
                    <th className="p-2 w-[90px]" title="Fake discount % applied on MRP">Fake Disc%</th>
                    <th className="p-2 w-[140px] text-right" title="Live preview: selling = cost × (1+gst%) × (1+margin%); MRP = selling ÷ (1 − fake_disc%)">Selling / MRP</th>
                    <th className="w-[40px]" /></tr></thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const isNew = !it.product_id;
                      const cost = parseFloat(it.unit_price) || 0;
                      const margin = parseFloat(it.margin_percent) || 0;
                      const gst = parseFloat(it.gst_percent) || 0;
                      const fakeDisc = Math.min(parseFloat(it.fake_discount_percent) || 0, 99);
                      const selling = cost > 0 ? Math.round(cost * (1 + gst / 100) * (1 + margin / 100) * 100) / 100 : 0;
                      const mrp = selling > 0 ? Math.round((selling / Math.max(1 - fakeDisc / 100, 0.01)) * 100) / 100 : 0;
                      return (
                      <tr key={i} className={`border-b border-slate-100 ${it._from_scan ? "bg-amber-50/50" : ""}`} data-testid={`pur-row-${i}`}>
                        <td className="p-1 align-top">
                          <div className="flex gap-1 items-center">
                            <Select value={it.product_id} onValueChange={(v) => {
                              const p = products.find((x) => x.id === v);
                              updateRow(i, { product_id: v, name: p?.name || it.name, barcode: p?.barcode || "", hsn_code: p?.hsn_code || it.hsn_code, gst_percent: p?.gst_percent ?? 5, margin_percent: p?.margin_percent ?? orgDefaults.margin_percent, fake_discount_percent: p?.fake_discount_percent ?? orgDefaults.fake_discount_percent });
                            }}>
                              <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`pur-row-product-trigger-${i}`}><SelectValue placeholder={isNew ? (it.name ? it.name.slice(0, 22) : "New") : "Pick"} /></SelectTrigger>
                              <SelectContent>{products.filter((p) => p.branch_id === activeBranchId).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                            <Button type="button" size="icon" variant="outline" className="rounded-none h-8 w-8 shrink-0" title="Add brand-new product"
                              onClick={() => { setProductQuick({ ...productQuick, name: it.name || "", hsn_code: it.hsn_code || "", cost_price: it.unit_price || 0, gst_percent: it.gst_percent || 5, margin_percent: it.margin_percent || orgDefaults.margin_percent, fake_discount_percent: it.fake_discount_percent || orgDefaults.fake_discount_percent, target_row: i }); setProductQuickOpen(true); }}
                              data-testid={`quick-add-product-${i}`}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          {isNew && it.name && <span className="text-[9px] uppercase tracking-widest text-amber-700 mt-1 inline-block">New on submit</span>}
                          {it._from_scan && <span className="text-[9px] uppercase tracking-widest text-violet-700 mt-1 ml-1 inline-block">· Scanned</span>}
                        </td>
                        <td className="p-1 align-top"><Input value={it.name} onChange={(e) => updateRow(i, { name: e.target.value })} className="rounded-none h-8 text-xs w-full" placeholder="Product name" data-testid={`pur-row-name-${i}`} /></td>
                        <td className="p-1 align-top"><Input value={it.description || ""} onChange={(e) => updateRow(i, { description: e.target.value })} className="rounded-none h-8 text-xs w-full" placeholder="Optional" data-testid={`pur-row-desc-${i}`} /></td>
                        <td className="p-1 align-top"><Input value={it.hsn_code} onChange={(e) => updateRow(i, { hsn_code: e.target.value })} className="rounded-none h-8 text-xs w-full" /></td>
                        <td className="p-1 align-top"><Input type="number" min="0" value={it.quantity} onFocus={(e) => { if (e.target.value === "0") updateRow(i, { quantity: "" }); }} onBlur={(e) => { if (e.target.value === "") updateRow(i, { quantity: 0 }); }} onChange={(e) => updateRow(i, { quantity: e.target.value === "" ? "" : (parseFloat(e.target.value) || 0) })} className="rounded-none h-8 text-xs w-full" /></td>
                        <td className="p-1 align-top"><Input type="number" step="0.01" min="0" value={it.unit_price} onFocus={(e) => { if (e.target.value === "0") updateRow(i, { unit_price: "" }); }} onBlur={(e) => { if (e.target.value === "") updateRow(i, { unit_price: 0 }); }} onChange={(e) => updateRow(i, { unit_price: e.target.value === "" ? "" : (parseFloat(e.target.value) || 0) })} className="rounded-none h-8 text-xs w-full" /></td>
                        <td className="p-1 align-top"><Input type="number" min="0" max="100" value={it.discount_percent} onChange={(e) => updateRow(i, { discount_percent: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-xs w-full" data-testid={`pur-row-disc-${i}`} /></td>
                        <td className="p-1 align-top"><Input type="number" min="0" value={it.gst_percent} onChange={(e) => updateRow(i, { gst_percent: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-xs w-full" /></td>
                        <td className="p-1 align-top"><Input type="number" min="0" value={it.margin_percent ?? ""} onChange={(e) => updateRow(i, { margin_percent: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-xs w-full" data-testid={`pur-row-margin-${i}`} title="Editable for both new and existing products" /></td>
                        <td className="p-1 align-top"><Input type="number" min="0" max="99" value={it.fake_discount_percent ?? ""} onChange={(e) => updateRow(i, { fake_discount_percent: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-xs w-full" data-testid={`pur-row-fakedisc-${i}`} title="Editable for both new and existing products" /></td>
                        <td className="p-1 align-top text-right tabular-nums text-xs" data-testid={`pur-row-mrp-preview-${i}`}>
                          {selling > 0 ? (
                            <div className="space-y-0.5">
                              <div className="text-slate-700">Sell <b>{formatINR(selling)}</b></div>
                              <div className="text-violet-700">MRP <b>{formatINR(mrp)}</b></div>
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-1 align-top"><Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeRow(i)}><Trash2 className="w-3 h-3" /></Button></td>
                      </tr>
                      );
                    })}
                    {form.items.length === 0 && <tr><td colSpan={12} className="p-4 text-center text-slate-400">Click "Row" to add items, or scan a PDF bill</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Party Discount %</Label><Input type="number" min="0" max="100" step="0.01" value={form.party_discount_percent} onFocus={(e) => { if (e.target.value === "0") setForm({ ...form, party_discount_percent: "" }); }} onBlur={(e) => { if (e.target.value === "") setForm({ ...form, party_discount_percent: 0 }); }} onChange={(e) => setForm({ ...form, party_discount_percent: e.target.value === "" ? "" : (parseFloat(e.target.value) || 0) })} className="rounded-none mt-1" data-testid="pur-party-disc-pct" /></div>
                <div><Label>Party Discount ₹</Label><Input type="number" min="0" step="0.01" value={form.party_discount_amount} onFocus={(e) => { if (e.target.value === "0") setForm({ ...form, party_discount_amount: "" }); }} onBlur={(e) => { if (e.target.value === "") setForm({ ...form, party_discount_amount: 0 }); }} onChange={(e) => setForm({ ...form, party_discount_amount: e.target.value === "" ? "" : (parseFloat(e.target.value) || 0) })} className="rounded-none mt-1" data-testid="pur-party-disc-amt" /></div>
                <div><Label>Round-off (₹) <span className="text-[10px] text-slate-400">leave blank for auto-round to ₹0.5</span></Label><Input type="number" step="0.01" value={form.round_off ?? ""} onChange={(e) => setForm({ ...form, round_off: e.target.value === "" ? null : (parseFloat(e.target.value) || 0) })} placeholder="auto" className="rounded-none mt-1" data-testid="pur-roundoff" /></div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1" /></div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={(e) => submit(e, true)} className="rounded-none" disabled={form.items.length === 0} data-testid="pur-save-draft">Save as Draft</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" disabled={form.items.length === 0} data-testid="pur-post">Post & Update Stock</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <DataTable
        endpoint="/purchase-bills"
        columns={[
          { key: "bill_number_sys", label: "System #", render: (p) => <span className="font-mono text-xs">{p.bill_number_sys}</span> },
          { key: "bill_number", label: "Bill #", sortable: true, render: (p) => <span className="font-mono text-xs">{p.bill_number}</span> },
          { key: "party_name", label: "Party", render: (p) => p.party_name },
          { key: "bill_date", label: "Date", sortable: true, render: (p) => <span className="text-slate-600">{p.bill_date}</span> },
          { key: "status", label: "Status", render: (p) => <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${p.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>{p.status || "posted"}</span> },
          { key: "grand_total", label: "Total", sortable: true, className: "text-right", render: (p) => <span className="font-semibold tabular-nums">{formatINR(p.grand_total)}</span> },
          { key: "imported_from_tally", label: "Src", render: (p) => p.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
        ]}
        filters={[
          { key: "status", type: "select", label: "Status", options: [{ value: "draft", label: "Draft" }, { value: "posted", label: "Posted" }] },
          { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
          { key: "date_from", type: "date", label: "From date" },
          { key: "date_to", type: "date", label: "To date" },
        ]}
        searchPlaceholder="Search bill #, party, notes..."
        extraQuery={{ branch_id: activeBranchId || undefined }}
        reloadKey={reloadKey}
        actions={(p) => (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setViewing(p)} data-testid={`view-purchase-${p.id}`}><Eye className="w-4 h-4" /></Button>
            {isSuper && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} data-testid={`edit-purchase-${p.id}`}><Pencil className="w-4 h-4" /></Button>}
            {p.status !== "draft" && (
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-violet-700 hover:text-violet-900 hover:bg-violet-50" onClick={() => printBillBarcodes(p.id)} title="Print barcode labels for all items on this bill" data-testid={`print-bill-barcodes-${p.id}`}><Printer className="w-4 h-4" /></Button>
            )}
            {p.status === "draft" && (
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => postDraft(p.id)} data-testid={`post-draft-${p.id}`}>Post</Button>
            )}
          </div>
        )}
        dataTestid="purchase-bills-table"
      />

      {/* View purchase bill dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Purchase Bill — {viewing?.bill_number}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">System No</Label><div className="font-mono">{viewing.bill_number_sys}</div></div>
                <div><Label className="text-xs">Bill No</Label><div className="font-mono">{viewing.bill_number}</div></div>
                <div><Label className="text-xs">Date</Label><div>{viewing.bill_date}</div></div>
                <div className="col-span-3"><Label className="text-xs">Party</Label><div className="font-medium">{viewing.party_name}</div></div>
              </div>
              <div className="border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50"><tr><th className="p-2 text-left">Name</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Disc%</th><th className="p-2 text-right">GST%</th><th className="p-2 text-right">Total</th></tr></thead>
                  <tbody>
                    {(viewing.items || []).map((it, i) => (
                      <tr key={i} className="border-t"><td className="p-2">{it.name}</td><td className="p-2 text-right">{it.quantity}</td><td className="p-2 text-right">{formatINR(it.unit_price)}</td><td className="p-2 text-right">{it.discount_percent || 0}%</td><td className="p-2 text-right">{it.gst_percent}%</td><td className="p-2 text-right">{formatINR(it.line_total)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                <div><Label className="text-xs">Subtotal</Label><div>{formatINR(viewing.subtotal)}</div></div>
                <div><Label className="text-xs">GST</Label><div>{formatINR(viewing.gst_total)}</div></div>
                <div><Label className="text-xs">Grand Total</Label><div className="font-bold">{formatINR(viewing.grand_total)}</div></div>
              </div>
              {viewing.edited_at && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">Last edited: {new Date(viewing.edited_at).toLocaleString("en-IN")}</div>}
              {viewing.status !== "draft" && (
                <div className="pt-2 border-t flex justify-end">
                  <Button onClick={() => printBillBarcodes(viewing.id)} className="rounded-none bg-violet-600 hover:bg-violet-700" data-testid="view-print-bill-barcodes">
                    <Printer className="w-4 h-4 mr-2" />Print Barcodes for All Items
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit purchase bill dialog — Super Admin only */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit Purchase Bill</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Bill Number</Label><Input value={editing.bill_number || ""} onChange={(e) => setEditing({ ...editing, bill_number: e.target.value })} className="rounded-none mt-1" data-testid="edit-pur-billno" /></div>
                <div><Label>Bill Date</Label><Input type="date" value={editing.bill_date || ""} onChange={(e) => setEditing({ ...editing, bill_date: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label>Party (read-only)</Label><Input readOnly value={editing.party_name} className="rounded-none mt-1 bg-slate-50" /></div>
              </div>
              <div className="border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50"><tr><th className="p-1 text-left">Name</th><th className="p-1">Qty</th><th className="p-1">Rate</th><th className="p-1">Disc%</th><th className="p-1">GST%</th><th /></tr></thead>
                  <tbody>
                    {(editing.items || []).map((it, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1"><Input value={it.name} onChange={(e) => { const next = [...editing.items]; next[i] = { ...next[i], name: e.target.value }; setEditing({ ...editing, items: next }); }} className="rounded-none h-7 text-xs" /></td>
                        <td className="p-1"><Input type="number" min="0" value={it.quantity} onChange={(e) => { const next = [...editing.items]; next[i] = { ...next[i], quantity: parseFloat(e.target.value) || 0 }; setEditing({ ...editing, items: next }); }} className="rounded-none h-7 text-xs w-16" /></td>
                        <td className="p-1"><Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => { const next = [...editing.items]; next[i] = { ...next[i], unit_price: parseFloat(e.target.value) || 0 }; setEditing({ ...editing, items: next }); }} className="rounded-none h-7 text-xs w-20" /></td>
                        <td className="p-1"><Input type="number" min="0" max="100" value={it.discount_percent || 0} onChange={(e) => { const next = [...editing.items]; next[i] = { ...next[i], discount_percent: parseFloat(e.target.value) || 0 }; setEditing({ ...editing, items: next }); }} className="rounded-none h-7 text-xs w-16" /></td>
                        <td className="p-1"><Input type="number" min="0" value={it.gst_percent} onChange={(e) => { const next = [...editing.items]; next[i] = { ...next[i], gst_percent: parseFloat(e.target.value) || 0 }; setEditing({ ...editing, items: next }); }} className="rounded-none h-7 text-xs w-16" /></td>
                        <td className="p-1"><Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => setEditing({ ...editing, items: editing.items.filter((_, idx) => idx !== i) })}><Trash2 className="w-3 h-3" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-none w-full text-xs"
                onClick={() => setEditing({
                  ...editing,
                  items: [...(editing.items || []), {
                    product_id: "", name: "", description: "", barcode: "", hsn_code: "",
                    quantity: 1, unit_price: 0, gst_percent: 5, discount_percent: 0,
                    margin_percent: orgDefaults.margin_percent,
                    fake_discount_percent: orgDefaults.fake_discount_percent,
                  }],
                })}
                data-testid="edit-pur-add-item"
              >
                + Add Item
              </Button>
              <div><Label>Notes</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="rounded-none mt-1" /></div>
              <div className="bg-amber-50 border border-amber-200 text-xs p-2 text-amber-800">Stock quantities will be recalculated. All edits are audit logged.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="edit-pur-save"
              onClick={async () => {
                try {
                  await api.put(`/purchase-bills/${editing.id}`, {
                    bill_number: editing.bill_number, bill_date: editing.bill_date,
                    notes: editing.notes,
                    party_discount_percent: editing.party_discount_percent || 0,
                    party_discount_amount: editing.party_discount_amount || 0,
                    items: editing.items,
                  });
                  toast.success("Purchase bill updated");
                  setEditing(null); load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-creation barcode print summary modal */}
      <Dialog open={!!createResult} onOpenChange={(v) => { if (!v) { setCreateResult(null); setBarcodeQty({}); } }}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Bill Posted · Print Barcode Labels</DialogTitle></DialogHeader>
          {createResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-amber-50 border border-amber-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700">Newly Created Products</div>
                  <div className="text-2xl font-bold text-amber-900">{createResult.created.length}</div>
                </div>
                <div className="bg-green-50 border border-green-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-green-700">Existing — Stock Updated</div>
                  <div className="text-2xl font-bold text-green-900">{createResult.updated.length}</div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Choose how many labels to print per product (default = quantity from this bill)</div>
                <div className="border border-slate-200 max-h-72 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0"><tr>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Barcode</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-right">From Bill</th>
                      <th className="p-2 text-right">Labels to Print</th>
                    </tr></thead>
                    <tbody>
                      {[...createResult.created.map((p) => ({ ...p, _new: true })), ...createResult.updated.map((p) => ({ ...p, _new: false }))].map((p) => {
                        const billItem = (createResult.bill?.items || []).find((it) => it.product_id === p.id);
                        const billQty = billItem ? Math.max(1, Math.round(billItem.quantity)) : 1;
                        return (
                          <tr key={p.id} className="border-t border-slate-100" data-testid={`barcode-row-${p.id}`}>
                            <td className="p-2">{p._new ? <span className="bg-amber-100 text-amber-800 text-[9px] px-2 py-0.5 uppercase tracking-widest">New</span> : <span className="bg-green-100 text-green-800 text-[9px] px-2 py-0.5 uppercase tracking-widest">Updated</span>}</td>
                            <td className="p-2 font-mono">{p.barcode}</td>
                            <td className="p-2">{p.name}</td>
                            <td className="p-2 text-right tabular-nums text-slate-600">{billQty}</td>
                            <td className="p-2 text-right">
                              <Input type="number" min="0" value={barcodeQty[p.id] ?? billQty}
                                onChange={(e) => setBarcodeQty({ ...barcodeQty, [p.id]: parseInt(e.target.value) || 0 })}
                                className="rounded-none h-8 text-xs w-20 inline-block" data-testid={`barcode-qty-${p.id}`} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Tip: Set qty to 0 to skip a product.</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-none" onClick={() => { setCreateResult(null); setBarcodeQty({}); }} data-testid="close-create-summary">Close</Button>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="print-barcodes-btn"
                  onClick={async () => {
                    const all = [...createResult.created, ...createResult.updated];
                    // Build {product_id, count} list (skip products with count 0)
                    const items = [];
                    all.forEach((p) => {
                      const billItem = (createResult.bill?.items || []).find((it) => it.product_id === p.id);
                      const defaultQty = billItem ? Math.max(1, Math.round(billItem.quantity)) : 1;
                      const count = (barcodeQty[p.id] !== undefined) ? barcodeQty[p.id] : defaultQty;
                      if (count > 0) items.push({ product_id: p.id, count });
                    });
                    const totalLabels = items.reduce((s, it) => s + it.count, 0);
                    if (totalLabels === 0) { toast.error("No labels to print"); return; }
                    try {
                      const resp = await api.post("/barcode-labels", { items }, { responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                      window.open(url, "_blank");
                      toast.success(`${totalLabels} labels generated`);
                    } catch (e) { toast.error("Could not generate barcode PDF"); }
                  }}>Print {Object.values(barcodeQty).reduce((s, v) => s + (v || 0), 0) || (createResult.bill?.items || []).reduce((s, it) => s + Math.max(1, Math.round(it.quantity)), 0)} Labels</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick-add Party dialog — full form like Parties page */}
      <Dialog open={partyQuickOpen} onOpenChange={setPartyQuickOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Quick Add Party / Supplier</DialogTitle></DialogHeader>
          <form onSubmit={submitQuickParty} className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 p-3">
              <Label>GSTIN (optional — leave blank for unregistered)</Label>
              <Input value={partyQuick.gstin} onChange={(e) => setPartyQuick({ ...partyQuick, gstin: e.target.value.toUpperCase() })} className="rounded-none mt-1 font-mono uppercase" maxLength={15} placeholder="29ABCDE1234F1Z5" data-testid="quick-party-gstin" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Party Name {!partyQuick.gstin && <span className="text-red-600">*</span>}</Label><Input required={!partyQuick.gstin} value={partyQuick.party_name} onChange={(e) => setPartyQuick({ ...partyQuick, party_name: e.target.value })} className="rounded-none mt-1" data-testid="quick-party-name" /></div>
              <div><Label>Trade Name</Label><Input value={partyQuick.trade_name} onChange={(e) => setPartyQuick({ ...partyQuick, trade_name: e.target.value })} className="rounded-none mt-1" /></div>
              <div className="col-span-2"><Label>Legal Business Name</Label><Input value={partyQuick.legal_name} onChange={(e) => setPartyQuick({ ...partyQuick, legal_name: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>State</Label><Input value={partyQuick.state} onChange={(e) => setPartyQuick({ ...partyQuick, state: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Taxpayer Type</Label><Input value={partyQuick.taxpayer_type} onChange={(e) => setPartyQuick({ ...partyQuick, taxpayer_type: e.target.value })} className="rounded-none mt-1" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={partyQuick.address} onChange={(e) => setPartyQuick({ ...partyQuick, address: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>City</Label><Input value={partyQuick.city} onChange={(e) => setPartyQuick({ ...partyQuick, city: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>PIN</Label><Input value={partyQuick.pin} onChange={(e) => setPartyQuick({ ...partyQuick, pin: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Contact Person</Label><Input value={partyQuick.contact_person} onChange={(e) => setPartyQuick({ ...partyQuick, contact_person: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Phone</Label><Input value={partyQuick.phone} onChange={(e) => setPartyQuick({ ...partyQuick, phone: e.target.value })} className="rounded-none mt-1" /></div>
              <div className="col-span-2"><Label>Email</Label><Input type="email" value={partyQuick.email} onChange={(e) => setPartyQuick({ ...partyQuick, email: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Bank Details (optional)</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Bank Name</Label><Input value={partyQuick.bank_name} onChange={(e) => setPartyQuick({ ...partyQuick, bank_name: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label>Account No.</Label><Input value={partyQuick.bank_account_no} onChange={(e) => setPartyQuick({ ...partyQuick, bank_account_no: e.target.value })} className="rounded-none mt-1 font-mono" /></div>
                <div><Label>IFSC</Label><Input value={partyQuick.ifsc} onChange={(e) => setPartyQuick({ ...partyQuick, ifsc: e.target.value.toUpperCase() })} className="rounded-none mt-1 font-mono" /></div>
              </div>
            </div>
            <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="quick-party-submit">Add Party</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick-add Product dialog — full form matching Products page */}
      <Dialog open={productQuickOpen} onOpenChange={setProductQuickOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Quick Add Product</DialogTitle></DialogHeader>
          <form onSubmit={submitQuickProduct} className="grid grid-cols-3 gap-3">
            <div className="col-span-3"><Label>Name *</Label><Input required value={productQuick.name} onChange={(e) => setProductQuick({ ...productQuick, name: e.target.value })} className="rounded-none mt-1" data-testid="quick-product-name" /></div>
            <div><Label>Category</Label>
              <Select value={CATEGORIES.includes(productQuick.category) ? productQuick.category : "__custom__"} onValueChange={(v) => { if (v === "__custom__") setProductQuick({ ...productQuick, category: "" }); else setProductQuick({ ...productQuick, category: v }); }}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value="__custom__">+ Add custom…</SelectItem>
                </SelectContent>
              </Select>
              {!CATEGORIES.includes(productQuick.category) && (
                <Input value={productQuick.category} onChange={(e) => setProductQuick({ ...productQuick, category: e.target.value })} placeholder="New category name" className="rounded-none mt-1" autoFocus />
              )}
            </div>
            <div><Label>Brand</Label><Input value={productQuick.brand} onChange={(e) => setProductQuick({ ...productQuick, brand: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Fabric Type</Label><Input value={productQuick.fabric_type} onChange={(e) => setProductQuick({ ...productQuick, fabric_type: e.target.value })} className="rounded-none mt-1" placeholder="Cotton, Silk..." /></div>
            <div><Label>Color</Label><Input value={productQuick.color} onChange={(e) => setProductQuick({ ...productQuick, color: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Size</Label><Input value={productQuick.size} onChange={(e) => setProductQuick({ ...productQuick, size: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Design Code</Label><Input value={productQuick.design_code} onChange={(e) => setProductQuick({ ...productQuick, design_code: e.target.value })} className="rounded-none mt-1 font-mono" /></div>
            <div><Label>Barcode (auto if blank)</Label><Input value={productQuick.barcode} onChange={(e) => setProductQuick({ ...productQuick, barcode: e.target.value })} className="rounded-none mt-1 font-mono" placeholder="TSS#####" /></div>
            <div><Label>HSN Code</Label><Input value={productQuick.hsn_code} onChange={(e) => setProductQuick({ ...productQuick, hsn_code: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="quick-product-hsn" /></div>
            <div><Label>SKU</Label><Input value={productQuick.sku} onChange={(e) => setProductQuick({ ...productQuick, sku: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Cost Price (₹) *</Label><Input required type="number" step="0.01" min="0" value={productQuick.cost_price} onChange={(e) => {
              const v = e.target.value === "" ? "" : (parseFloat(e.target.value) || 0);
              const sell = computeSelling(v, productQuick.margin_percent);
              const mrp = computeMrp(sell, productQuick.fake_discount_percent);
              setProductQuick({ ...productQuick, cost_price: v, mrp });
            }} className="rounded-none mt-1" data-testid="quick-product-cost" /></div>
            <div><Label>Margin %</Label><Input type="number" step="0.01" min="0" value={productQuick.margin_percent} onChange={(e) => {
              const v = e.target.value === "" ? "" : (parseFloat(e.target.value) || 0);
              const sell = computeSelling(productQuick.cost_price, v);
              const mrp = computeMrp(sell, productQuick.fake_discount_percent);
              setProductQuick({ ...productQuick, margin_percent: v, mrp });
            }} className="rounded-none mt-1" /></div>
            <div><Label>Fake Discount %</Label><Input type="number" step="0.01" min="0" max="99" value={productQuick.fake_discount_percent} onChange={(e) => {
              const v = e.target.value === "" ? "" : (parseFloat(e.target.value) || 0);
              const sell = computeSelling(productQuick.cost_price, productQuick.margin_percent);
              const mrp = computeMrp(sell, v);
              setProductQuick({ ...productQuick, fake_discount_percent: v, mrp });
            }} className="rounded-none mt-1" /></div>
            <div><Label>Selling Price (₹) — auto</Label><Input readOnly value={computeSelling(productQuick.cost_price, productQuick.margin_percent).toFixed(2)} className="rounded-none mt-1 bg-slate-50" /></div>
            <div><Label>MRP (₹) — auto, editable</Label><Input type="number" step="0.01" min="0" value={productQuick.mrp} onChange={(e) => setProductQuick({ ...productQuick, mrp: e.target.value === "" ? "" : (parseFloat(e.target.value) || 0) })} className="rounded-none mt-1" /></div>
            <div><Label>Stock</Label><Input type="number" min="0" value={productQuick.stock} onChange={(e) => setProductQuick({ ...productQuick, stock: e.target.value === "" ? "" : (parseInt(e.target.value) || 0) })} className="rounded-none mt-1" /></div>
            <div><Label>GST %</Label><Input type="number" step="0.01" min="0" value={productQuick.gst_percent} onChange={(e) => setProductQuick({ ...productQuick, gst_percent: e.target.value === "" ? "" : (parseFloat(e.target.value) || 0) })} className="rounded-none mt-1" /></div>
            <div><Label>Low Stock Alert</Label><Input type="number" min="0" value={productQuick.low_stock_alert} onChange={(e) => setProductQuick({ ...productQuick, low_stock_alert: e.target.value === "" ? "" : (parseInt(e.target.value) || 0) })} className="rounded-none mt-1" /></div>
            <div className="col-span-3 text-[10px] text-slate-500">Barcode (TSS{"{seq}"}) auto-generates if blank. MRP / Selling Price computed from Cost × Margin × Fake-Discount.</div>
            <DialogFooter className="col-span-3"><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="quick-product-submit">Add Product</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
